import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { formatToPar } from '../lib/format'
import { usePageTitle } from '../lib/usePageTitle'

/**
 * Draftboarden: vem tog vem.
 *
 * Ingen ny backend. team_leaderboard levererar redan varje lags sex spelare
 * när de avslöjats, så det här är en omvänd tabell av data som redan finns —
 * och RLS avgör fortfarande om något syns alls (teams_revealed).
 *
 * Sorterad på spelarnas score, bäst först. Då läses den uppifrån som "vilka
 * valde de bästa" och nerifrån som "vem sitter med sänkena" — det var så
 * frågan ställdes när sorteringen på antal val ratades under Wyndham 2026.
 * Överlappet syns ändå i Tagen av-kolumnen.
 */
interface TeamRow {
  team_id: string
  display_name: string
  players: Array<{
    dg_id: number
    full_name: string
    country: string | null
    total_to_par: number | null
    status: string | null
  }>
}

interface PlayerPick {
  dg_id: number
  full_name: string
  country: string | null
  total_to_par: number | null
  status: string | null
  takenBy: string[]
}

export function Draftboard() {
  const { id = '' } = useParams()
  const [teams, setTeams] = useState<TeamRow[]>([])
  const [name, setName] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  usePageTitle(name ? `Draftboard ${name}` : 'Draftboard')

  const load = useCallback(async () => {
    const [t, lb] = await Promise.all([
      supabase.from('tournaments').select('name').eq('id', id).maybeSingle(),
      supabase
        .from('team_leaderboard')
        .select('team_id, display_name, players')
        .eq('tournament_id', id),
    ])
    if (t.data) setName((t.data as { name: string }).name)
    if (lb.error) setError(lb.error.message)
    else setTeams((lb.data as unknown as TeamRow[]) ?? [])
    setLoading(false)
  }, [id])

  useEffect(() => {
    void load()
  }, [load])

  const picks = useMemo<PlayerPick[]>(() => {
    const map = new Map<number, PlayerPick>()
    for (const t of teams) {
      for (const p of t.players ?? []) {
        const found = map.get(p.dg_id)
        if (found) found.takenBy.push(t.display_name)
        else
          map.set(p.dg_id, {
            dg_id: p.dg_id,
            full_name: p.full_name,
            country: p.country,
            total_to_par: p.total_to_par,
            status: p.status,
            takenBy: [t.display_name],
          })
      }
    }
    // Spelare utan score (inte startat än) sist; vid lika score överst den
    // som flest valt. Null-jämförelsen är utskriven — Infinity - Infinity
    // blir NaN och ger odefinierad ordning.
    const cmpScore = (a: number | null, b: number | null) => {
      if (a === null && b === null) return 0
      if (a === null) return 1
      if (b === null) return -1
      return a - b
    }
    return [...map.values()].sort(
      (a, b) =>
        cmpScore(a.total_to_par, b.total_to_par) || b.takenBy.length - a.takenBy.length,
    )
  }, [teams])

  const unika = picks.filter((p) => p.takenBy.length === 1)

  return (
    <div className="page stack">
      <div>
        <p className="eyebrow">{name || 'Tävlingen'}</p>
        <h1>Draftboard</h1>
      </div>

      {error && <div className="notice notice--error">{error}</div>}
      {loading && <p className="muted">Hämtar…</p>}

      {!loading && teams.length === 0 && (
        <div className="card">
          <p className="muted">
            Inga lag att visa än. Laguppställningarna blir synliga först vid
            tee-off — före det är de hemliga för alla utom ägaren.
          </p>
        </div>
      )}

      {picks.length > 0 && (
        <>
          <div className="card stack">
            <p className="muted">
              {picks.length} olika spelare valda av {teams.length} lag.
              {unika.length > 0 && ` ${unika.length} av dem tog bara en person.`}
            </p>
          </div>

          <div className="board">
            <div className="marathon__head">
              <span className="marathon__cell">Tagen av</span>
              <span className="board__name">Spelare</span>
              <span className="marathon__score">Score</span>
            </div>

            {picks.map((p) => (
              <div key={p.dg_id} className="marathon__row">
                <span className="marathon__cell">
                  {p.takenBy.length}/{teams.length}
                </span>
                <span className="board__name">
                  {p.full_name}
                  <span className="board__player-meta"> {p.country}</span>
                  <span className="draft__takers">{p.takenBy.join(', ')}</span>
                </span>
                <span
                  className={`marathon__score${(p.total_to_par ?? 0) < 0 ? ' board__score--under' : ''}`}
                >
                  {p.status === 'cut' ? 'CUT' : formatToPar(p.total_to_par)}
                </span>
              </div>
            ))}
          </div>
        </>
      )}

      <div className="card">
        <p className="muted">
          <Link to={`/turnering/${id}/leaderboard`}>Till leaderboarden</Link>
        </p>
      </div>
    </div>
  )
}
